import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { IS_AWS_MODE } from '../config/frontend';
import {
  authenticateWithCognito,
  changeCognitoPassword,
  FrontendUser,
  restoreCognitoSession,
  signOutCognito,
} from '../auth/cognito';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

type User = FrontendUser;

interface LoginResponse {
  access_token?: string;
  id_token?: string;
  token_type?: string;
  code?: string;
  temp_token?: string;
  user_id?: string;
  message?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string, otp?: string) => Promise<LoginResponse>;
  logout: () => void;
  setupTOTP: (tempToken: string) => Promise<{secret: string; otpauth_url: string; recovery_codes: string[]}>;
  verifyTOTP: (tempToken: string, otp: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (IS_AWS_MODE) {
        try {
          const result = await restoreCognitoSession();
          if (result) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${result.idToken}`;
            setToken(result.idToken);
            setUser(result.user);
          }
        } catch {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
          setUser(null);
        } finally {
          setLoading(false);
        }
        return;
      }

      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          const response = await axios.get(`${API}/users/me`);
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string, otp?: string): Promise<LoginResponse> => {
    try {
      if (IS_AWS_MODE) {
        const result = await authenticateWithCognito(email, password);
        axios.defaults.headers.common['Authorization'] = `Bearer ${result.idToken}`;
        setToken(result.idToken);
        setUser(result.user);
        toast.success('Connexion AWS réussie.');
        return { id_token: result.idToken, token_type: 'bearer' };
      }

      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        otp
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const { access_token } = response.data;
      
      if (access_token) {
        localStorage.setItem('token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);

        // Fetch user data
        const userResponse = await axios.get(`${API}/users/me`);
        setUser(userResponse.data);
        
        toast.success('Login successful!');
        return { access_token, token_type: 'bearer' };
      }

      return response.data;
    } catch (error: any) {
      if (IS_AWS_MODE) throw error;
      if (error.response?.status === 423) {
        // TOTP setup required or password change required
        return error.response.data.detail;
      } else if (error.response?.status === 401) {
        // TOTP required or invalid credentials
        return error.response.data.detail;
      } else {
        const message = error.response?.data?.detail || 'Login failed';
        toast.error(message);
        throw error;
      }
    }
  };

  const setupTOTP = async (tempToken: string) => {
    if (IS_AWS_MODE) {
      throw new Error('La configuration TOTP doit être effectuée avec Cognito.');
    }
    try {
      const response = await axios.post(`${API}/auth/setup-totp`, {}, {
        headers: {
          'Authorization': `Bearer ${tempToken}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.detail || 'TOTP setup failed';
      toast.error(message);
      throw error;
    }
  };

  const verifyTOTP = async (tempToken: string, otp: string) => {
    if (IS_AWS_MODE) {
      throw new Error('La vérification TOTP doit être effectuée avec Cognito.');
    }
    try {
      const response = await axios.post(`${API}/auth/verify-totp`, {
        temp_token: tempToken,
        otp
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);

      // Fetch user data
      const userResponse = await axios.get(`${API}/users/me`);
      setUser(userResponse.data);
      
      toast.success('Two-factor authentication enabled successfully!');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'TOTP verification failed';
      toast.error(message);
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      if (IS_AWS_MODE) {
        await changeCognitoPassword(currentPassword, newPassword);
        toast.success('Mot de passe Cognito modifié.');
        return;
      }

      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      toast.success('Password changed successfully!');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Password change failed';
      toast.error(message);
      throw error;
    }
  };

  const logout = () => {
    if (IS_AWS_MODE) signOutCognito();
    else localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setToken(null);
    toast.info('Logged out successfully');
  };

  const value = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    loading,
    login,
    logout,
    setupTOTP,
    verifyTOTP,
    changePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
